import type { Channel, ChannelMemberResponse, Event, StreamChat } from 'stream-chat';

type StreamAttachment = {
    asset_url?: string;
    image_url?: string;
    thumb_url?: string;
    mime_type?: string;
    type?: string;
    title?: string;
};

type StreamMessage = {
    id?: string;
    created_at?: string | Date;
    text?: string;
    type?: string;
    custom?: Record<string, unknown>;
    custom_type?: string;
    severity?: string;
    event?: string;
    attachments?: StreamAttachment[];
    user?: {
        id?: string;
        name?: string;
    };
};

export function streamEventChannelCid(event: Event): string | undefined {
    const eventWithCid = event as Event & { cid?: string };
    return event.channel?.cid || eventWithCid.cid;
}

const readStreamMessageCustom = (message: StreamMessage) => {
    const custom = { ...(message?.custom || {}) };
    const customType =
        custom.customType ||
        custom.custom_type ||
        message?.custom_type ||
        (message?.type === 'system' ? message.type : undefined);
    if (customType && !custom.customType) {
        custom.customType = customType;
    }
    if (message?.event && !custom.event) {
        custom.event = message.event;
    }
    if (message?.severity && !custom.severity) {
        custom.severity = message.severity;
    }
    return custom;
};

export function adaptStreamMessage(message: StreamMessage) {
    const custom = readStreamMessageCustom(message);
    const attachment = message?.attachments?.[0];
    const attachmentUrl = attachment?.asset_url || attachment?.image_url || attachment?.thumb_url;
    const attachmentType = attachment?.mime_type || (attachment?.type === 'image' ? 'image/jpeg' : undefined);
    const customType =
        (typeof custom.customType === 'string' && custom.customType) ||
        (typeof custom.custom_type === 'string' && custom.custom_type) ||
        message?.custom_type ||
        message?.type;
    return {
        messageId: message?.id,
        createdAt: message?.created_at ? new Date(message.created_at).getTime() : Date.now(),
        message: message?.text || '',
        customType,
        data: Object.keys(custom).length ? JSON.stringify(custom) : undefined,
        sender: {
            userId: message?.user?.id,
            nickname: message?.user?.name,
        },
        url: attachmentUrl,
        type: attachmentType,
        name: attachment?.title,
    };
}

export function wrapStreamChannel(client: StreamChat, channel: Channel) {
    const collectionHolder: {
        handler: {
            onMessagesAdded?: (
                context: { source: string },
                wrappedChannel: ReturnType<typeof wrapStreamChannel>,
                messages: ReturnType<typeof adaptStreamMessage>[]
            ) => void;
            onMessagesUpdated?: (
                context: { source: string },
                wrappedChannel: ReturnType<typeof wrapStreamChannel>,
                messages: ReturnType<typeof adaptStreamMessage>[]
            ) => void;
        } | null;
    } = { handler: null };

    const wrapped = {
        _stream: channel,
        _url: channel.cid,
        url: channel.cid,
        name: (channel.data as { name?: string } | undefined)?.name,
        get unreadMessageCount() {
            return channel.countUnread();
        },
        get lastMessage() {
            const messages = channel.state.messages || [];
            const last = messages[messages.length - 1];
            if (!last) {
                return null;
            }
            const adapted = adaptStreamMessage(last as StreamMessage);
            return {
                message: adapted.message,
                createdAt: adapted.createdAt,
            };
        },
        get data() {
            return JSON.stringify(channel.data || {});
        },
        get members() {
            return Object.values(channel.state.members || {}).map((member: ChannelMemberResponse) => ({
                userId: member.user?.id || member.user_id || '',
                nickname: member.user?.name || member.user?.id || member.user_id || '',
            }));
        },
        markAsRead: async () => {
            await channel.markRead();
        },
        sendUserMessage: (params: { message?: string; customType?: string; data?: string }) => {
            const callbacks: {
                onSucceeded: (fn: (message: ReturnType<typeof adaptStreamMessage>) => void) => typeof callbacks;
                onFailed: (fn: (error: unknown) => void) => typeof callbacks;
                _success?: (message: ReturnType<typeof adaptStreamMessage>) => void;
                _failed?: (error: unknown) => void;
            } = {
                onSucceeded: fn => {
                    callbacks._success = fn;
                    return callbacks;
                },
                onFailed: fn => {
                    callbacks._failed = fn;
                    return callbacks;
                },
            };
            const custom = params.data ? JSON.parse(params.data) : {};
            if (params.customType) {
                custom.customType = params.customType;
            }
            channel
                .sendMessage({
                    text: params.message || '',
                    type: 'regular',
                    custom,
                })
                .then(response => {
                    const adapted = adaptStreamMessage(response.message as StreamMessage);
                    if (params.customType) {
                        adapted.customType = params.customType;
                    }
                    collectionHolder.handler?.onMessagesAdded?.({ source: 'EVENT_MESSAGE_SENT' }, wrapped, [adapted]);
                    callbacks._success?.(adapted);
                })
                .catch(error => callbacks._failed?.(error));
            return callbacks;
        },
        createPreviousMessageListQuery: ({ limit }: { limit: number }) => {
            let exhausted = false;
            return {
                load: async () => {
                    if (exhausted) {
                        return [];
                    }
                    const response = await channel.query({
                        messages: { limit },
                    });
                    const messages = (response.messages || []).map(message =>
                        adaptStreamMessage(message as StreamMessage)
                    );
                    if (messages.length < limit) {
                        exhausted = true;
                    }
                    return messages;
                },
            };
        },
        createMessageCollection: () => {
            const onNew = (event: Event) => {
                if (!event.message) {
                    return;
                }
                const eventCid = streamEventChannelCid(event);
                if (eventCid && eventCid !== channel.cid) {
                    return;
                }
                const adapted = [adaptStreamMessage(event.message as StreamMessage)];
                collectionHolder.handler?.onMessagesAdded?.({ source: 'EVENT_MESSAGE_RECEIVED' }, wrapped, adapted);
            };
            const onUpdated = (event: Event) => {
                if (!event.message) {
                    return;
                }
                const eventCid = streamEventChannelCid(event);
                if (eventCid && eventCid !== channel.cid) {
                    return;
                }
                collectionHolder.handler?.onMessagesUpdated?.({ source: 'EVENT_MESSAGE_UPDATED' }, wrapped, [
                    adaptStreamMessage(event.message as StreamMessage),
                ]);
            };
            channel.on('message.new', onNew);
            channel.on('message.updated', onUpdated);
            return {
                setMessageCollectionHandler: (handler: NonNullable<typeof collectionHolder.handler>) => {
                    collectionHolder.handler = handler;
                },
                dispose: () => {
                    channel.off('message.new', onNew);
                    channel.off('message.updated', onUpdated);
                    if (collectionHolder.handler) {
                        collectionHolder.handler = null;
                    }
                },
            };
        },
    };
    return wrapped;
}

export async function queryWrappedChannels(client: StreamChat, userId: string) {
    const channels = await client.queryChannels(
        { type: 'messaging', members: { $in: [userId] } },
        { last_message_at: -1 },
        { watch: true, state: true, limit: 100 }
    );
    return channels.map(channel => wrapStreamChannel(client, channel));
}

export async function findWrappedChannelForUser(client: StreamChat, userId: string, targetUserId: string) {
    const normalizedTarget = targetUserId.trim();
    if (!normalizedTarget) {
        return null;
    }
    const channels = await queryWrappedChannels(client, userId);
    return channels.find(channel => channel.members.some(member => String(member.userId) === normalizedTarget)) || null;
}

export async function findWrappedSupportChannel(client: StreamChat, jobId: string) {
    const normalizedJobId = jobId.trim();
    if (!normalizedJobId) {
        return null;
    }
    const channel = client.channel('messaging', `support_${normalizedJobId}`);
    try {
        await channel.watch();
        return wrapStreamChannel(client, channel);
    } catch {
        return null;
    }
}

export async function createDistinctWrappedChannel(client: StreamChat, userIds: string[]) {
    const members = [...new Set(userIds.map(id => id.trim()).filter(Boolean))].sort();
    if (members.length < 2) {
        throw new Error('At least two Stream user ids are required to create a channel.');
    }
    // Do NOT set created_by_id here: with client-side (user token) auth Stream rejects it
    // ("Setting data.created_by_id is only allowed when using server side auth"). The creator
    // is automatically the connected user (the admin).
    const channel = client.channel('messaging', {
        members,
    });
    try {
        await channel.create();
    } catch {
        await channel.watch();
    }
    return wrapStreamChannel(client, channel);
}
